import React, { PureComponent } from 'react'
import { range, utcFormat, utcMonth, utcDay } from 'd3'
import './DatePicker.css'

const [MONTH, YEAR] = ['month', 'year']
const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

class DatePicker extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      previewDate: props.selectedDate,
      expanded: null
    }
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleCancel = this.handleCancel.bind(this)
    this.handleSelect = this.handleSelect.bind(this)
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedDate.valueOf() !== this.props.selectedDate.valueOf()) {
      this.setState({ previewDate: nextProps.selectedDate })
    }
  }
  handleSubmit() {
    const { onSubmit } = this.props
    const { previewDate } = this.state
    onSubmit(previewDate)
  }
  handleCancel() {
    const { onCancel } = this.props
    onCancel()
  }
  handleSelect(year, month, day) {
    const { availableTimeRange } = this.props
    const [available0, available1] = availableTimeRange
    const selectedDate = new Date(Date.UTC(year, month, day))
    this.setState({
      previewDate: new Date(Math.max(available0, Math.min(available1, selectedDate))),
      expandedPicker: null
    })
  }
  render() {
    const { availableTimeRange, message } = this.props
    const { expandedPicker } = this.state
    const date = this.state.previewDate 
    const dayWidth = 25
    const currentYear = (new Date()).getUTCFullYear()
    const monthFormat = (dt) => {
      return months[dt.getMonth()]
    }

    const day = date.getUTCDate()
    const month = date.getUTCMonth()
    const year = date.getUTCFullYear()


    return (
      <div
        className='DatePicker'
      >
        <div className='DatePicker-message'>{message}</div>
        <Picker
          className='DatePicker-month-picker'
          options={
            range(0, 12)
              .filter(monthIndex => (
                Date.UTC(year, monthIndex+1, 1) >= availableTimeRange[0] && 
                Date.UTC(year, monthIndex, 1) <= availableTimeRange[1]
              ))
              .map(monthIndex => ({
                id: monthIndex,
                label: monthFormat( new Date(currentYear, monthIndex, 1) )
              }) )
          }
          selected={month}
          onSelect={month => this.handleSelect(year, month, day)}
          onChangeExpand={expanded => this.setState({ expandedPicker: expanded ? MONTH : null})}
          isExpanded={expandedPicker === MONTH}
        />
        <Picker
          className='DatePicker-year-picker'
          options={
            range(1990, currentYear + 1)
              .filter(year => year >= availableTimeRange[0].getUTCFullYear() && year <= availableTimeRange[1].getUTCFullYear())
              .map(year => ({ id: year, label: String(year) }))
          }
          selected={year}
          onSelect={year => this.handleSelect(year, month, day)}
          onChangeExpand={expanded => this.setState({ expandedPicker: expanded ? YEAR : null})}
          isExpanded={expandedPicker === YEAR}
        />
        <DayOfMonthPicker
          dayWidth={dayWidth}
          options={{ month, year }}
          availableTimeRange={availableTimeRange}
          selected={day}
          onSelect={day => this.handleSelect(year, month, day)}
        />
        <div 
          className='DatePicker-btn DatePicker-cancel-btn'
          onClick={this.handleCancel}
        >Cancel</div>
        <div 
          className='DatePicker-btn DatePicker-submit-btn'
          onClick={this.handleSubmit}
        >Apply</div>
      </div>
    )
  }
}
DatePicker.defaultProps = {
  availableTimeRange: [-Infinity, Infinity]
}

class DayOfMonthPicker extends PureComponent {
  render() {
    const { options, selected, onSelect, dayWidth, availableTimeRange } = this.props
    const { month, year} = options
    const [available0, available1] = availableTimeRange
    const date0 = new Date(year, month, 1)
    const date1 = utcMonth.offset(date0, 1)
    const numDaysInMonth = utcDay.count(date0, date1)
    const dayOfWeek0 = date0.getUTCDay()

    const dayFormat = utcFormat('%a')
    const aSunday = new Date(1970, 0, 4) // a date that's known to be Sunday
    return <div
      className='DayOfMonthPicker'
      style={{width: dayWidth * 7}}
    >
      <ul className='DayOfMonthPicker-day-names'>{
        range(0, 7)
          .map(i => dayFormat( utcDay.offset(aSunday, i) ))
          .map(day => (
            <li
              key={day}
              style={{width: dayWidth}}
            >{day}</li>
          ))
      }</ul>
      <ul>
        <div style={{width: dayOfWeek0 * dayWidth}} />
        {
          range(1, numDaysInMonth + 1)
            .map(dayOfMonth => new Date(Date.UTC(year, month, dayOfMonth)))
            .map(date => {
              const day = date.getUTCDate()
              return (
                <li
                  key={day}
                  className={
                    (day === selected ? 'DayOfMonthPicker-selected-day' : '') + ' ' +
                    (date >= available0 && date <= available1 ? '' : 'DayOfMonthPicker-unavailable')
                  }
                  style={{
                    width: dayWidth,
                    lineHeight: dayWidth + 'px'
                  }}
                  onClick={() => onSelect(day)}
                >{day}</li>
              )
            })
        }
      </ul>
    </div>
  }
}

class Picker extends PureComponent {

  handleClickDecrement() { this.incrementSelectedOption(-1) }
  handleClickIncrement() { this.incrementSelectedOption(1) }

  incrementSelectedOption(direction) {
    const { options, selected, onSelect } = this.props
    const currentIndex = options.reduce(
      (foundIndex, option, i) => foundIndex === -1 ? option.id === selected ? i : foundIndex : foundIndex
    , -1)
    const newIndex = Math.max(0, Math.min(options.length - 1, currentIndex + direction))
    onSelect(options[newIndex].id)
  }

  handleClickValue() { this.props.onChangeExpand(!this.props.isExpanded) }

  render() {
    const { options, selected, className, isExpanded, onSelect } = this.props
    const selectedOption = options.find(option => option.id === selected)
    return (
      <div
        className={`DatePicker-picker ${className}`}
      >
        <div 
          className='DatePicker-increment-btn'
          onClick={this.handleClickDecrement.bind(this)}
        >◄</div>
        <div
          className='DatePicker-value'
          onClick={this.handleClickValue.bind(this)}
        >{selectedOption.label}</div>
        { isExpanded ? <ul>{
          options.map(option => (
            <li 
              key={option.id} 
              className={option === selectedOption ? 'selected' : '' }
              onClick={() => onSelect(option.id)}
            >{option.label}</li>
          ))
        }</ul> : null}
        <div 
          className='DatePicker-increment-btn'
          onClick={this.handleClickIncrement.bind(this)}
        >►</div>
      </div>
    )
  }
}

export default DatePicker


// WEBPACK FOOTER //
// ./src/DatePicker.js